import React, { useState } from 'react';
import { createPlan, updatePlan, Plan } from '../services/planService';
import { toast } from 'react-toastify';

interface PlanFormProps {
  initial?: Plan | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface FormState {
  planId?: number;
  planName: string;
  description: string;
  price: number;
  durationInDays: number;
  isActive: boolean;
  isDefault: boolean;
}

const defaultState: FormState = {
  planName: '',
  description: '',
  price: 0,
  durationInDays: 30,
  isActive: true,
  isDefault: false,
};

const PlanForm: React.FC<PlanFormProps> = ({ initial, onSuccess, onCancel }) => {
  const [form, setForm] = useState<FormState>(initial ? { ...initial } : defaultState);
  const [saving, setSaving] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setForm(f => ({ ...f, [name]: (e.target as HTMLInputElement).checked }));
    } else if (type === 'number') {
      setForm(f => ({ ...f, [name]: Number(value) }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const validate = (): string | null => {
    if (!form.planName.trim()) return 'Plan name required';
    if (form.price < 0) return 'Price cannot be negative';
    if (form.durationInDays <= 0) return 'Duration must be positive';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      if (form.planId) {
        await updatePlan(form.planId, form);
        toast.success('Plan updated');
      } else {
        await createPlan(form);
        toast.success('Plan created');
      }
      onSuccess();
    } catch {
      toast.error('Failed to save plan');
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-lg mx-auto bg-white p-6 rounded shadow">
      <h2 className="text-xl font-bold mb-4">{form.planId ? 'Edit Plan' : 'Create Plan'}</h2>
      <div className="mb-4">
        <label className="block mb-1 font-medium">Plan Name *</label>
        <input name="planName" value={form.planName} onChange={handleChange} className="w-full border rounded px-3 py-2" required />
      </div>
      <div className="mb-4">
        <label className="block mb-1 font-medium">Description</label>
        <textarea name="description" value={form.description} onChange={handleChange} className="w-full border rounded px-3 py-2" rows={3} />
      </div>
      <div className="mb-4">
        <label className="block mb-1 font-medium">Price (₹) *</label>
        <input type="number" name="price" value={form.price} onChange={handleChange} className="w-full border rounded px-3 py-2" min={0} required />
      </div>
      <div className="mb-4">
        <label className="block mb-1 font-medium">Duration (days) *</label>
        <input type="number" name="durationInDays" value={form.durationInDays} onChange={handleChange} className="w-full border rounded px-3 py-2" min={1} required />
      </div>
      <div className="flex items-center gap-6 mb-4">
        <label className="flex items-center gap-2">
          <input type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} /> Active
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" name="isDefault" checked={form.isDefault} onChange={handleChange} /> Default Plan
        </label>
      </div>
      <div className="flex gap-4 mt-6">
        <button type="submit" disabled={saving} className="bg-primary text-white px-4 py-2 rounded hover:bg-secondary transition">
          {saving ? 'Saving...' : form.planId ? 'Update' : 'Create'}
        </button>
        <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </form>
  );
};

export default PlanForm;
